import { useState, useRef, useEffect } from "react";
import { useAuth, apiClient } from "../App";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { 
  Palette, ArrowLeft, Upload, Image as ImageIcon, Loader2, 
  Download, Sparkles, Crown, Lock, Camera, Trash2, Check
} from "lucide-react";

const PhotoToColoring = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [style, setStyle] = useState("detailed");
  const [isConverting, setIsConverting] = useState(false);
  const [resultImage, setResultImage] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [usageInfo, setUsageInfo] = useState(null);

  // Photo-to-coloring is available for basic, plus, and pro (with different limits)
  const hasPhotoAccess = user?.subscription_tier === "basic" || 
                         user?.subscription_tier === "plus" || 
                         user?.subscription_tier === "pro";

  // Fetch usage info on mount
  useEffect(() => {
    const fetchUsage = async () => {
      if (!user) return;
      try {
        const response = await apiClient.get("/usage");
        setUsageInfo(response.data);
      } catch (error) {
        console.error("Failed to fetch usage:", error);
      }
    };
    fetchUsage();
  }, [user]);

  const styles = [
    { 
      id: "detailed", 
      label: "Detailed", 
      desc: "Intricate lines for adult coloring",
      icon: "✨"
    },
    { 
      id: "simple", 
      label: "Simple", 
      desc: "Bold outlines for kids",
      icon: "🎨"
    },
    { 
      id: "cartoon", 
      label: "Cartoon", 
      desc: "Fun, playful style",
      icon: "🎪"
    }
  ];

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image size must be less than 10MB");
      return;
    }

    setSelectedImage(file);
    setResultImage(null);
    setProjectId(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const input = fileInputRef.current;
      if (input) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        input.files = dataTransfer.files;
        handleFileSelect({ target: { files: dataTransfer.files } });
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const clearImage = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
    setResultImage(null);
    setProjectId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleConvert = async () => {
    if (!selectedImage || !previewUrl) {
      toast.error("Please select an image first");
      return;
    }

    if (!hasPhotoAccess) {
      toast.error("This feature requires Basic, Plus or Pro plan");
      navigate("/pricing");
      return;
    }

    // Check if user has remaining conversions
    if (usageInfo && usageInfo.photo_conversions_remaining <= 0) {
      toast.error("You've used all your photo conversions this month. Upgrade for more!");
      navigate("/pricing");
      return;
    }

    setIsConverting(true);

    try {
      // Get base64 data (remove the data:image/... prefix)
      const base64Data = previewUrl.split(",")[1];

      const response = await apiClient.post(
        "/generate/photo-to-coloring",
        {
          image_base64: base64Data,
          style: style
        },
        { 
          timeout: 120000 // 2 minute timeout
        }
      );

      setResultImage(response.data.image_url);
      setProjectId(response.data.project_id);
      
      // Update usage info
      setUsageInfo(prev => prev ? {
        ...prev,
        photo_conversions_used: response.data.photo_conversions_used,
        photo_conversions_remaining: response.data.photo_conversions_remaining
      } : null);
      
      toast.success("Photo converted to coloring page!");
    } catch (error) {
      console.error("Conversion error:", error);
      console.error("Error response:", error.response);
      console.error("Error data:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || "This feature requires a paid plan");
        navigate("/pricing");
      } else if (error.response?.status === 402) {
        toast.error(error.response?.data?.detail || "You've reached your conversion limit");
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error("Request timed out. The image may be too large or complex. Please try again.");
      } else {
        const errorMsg = error.response?.data?.detail || error.message || "Failed to convert photo. Please try again.";
        toast.error(errorMsg);
      }
    } finally {
      setIsConverting(false);
    }
  };

  const handleDownload = () => {
    if (!resultImage) return;
    
    const link = document.createElement("a");
    link.href = resultImage;
    link.download = `coloring-page-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Download started!");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-pink-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Link 
                to="/dashboard" 
                className="text-slate-600 hover:text-slate-900 font-medium flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Dashboard
              </Link>
              {user?.subscription_tier && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-violet-100 to-pink-100 rounded-full">
                  <Crown className="w-4 h-4 text-violet-600" />
                  <span className="text-sm font-medium text-violet-700 capitalize">
                    {user.subscription_tier} Plan
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-100 to-pink-100 rounded-full mb-4">
              <Camera className="w-4 h-4 text-violet-600" />
              <span className="text-sm font-medium text-violet-700">Premium Feature</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Photo to Coloring Page
            </h1>
            <p className="text-slate-600 max-w-xl mx-auto">
              Transform your photos into beautiful line art coloring pages. 
              Upload any image and watch the magic happen!
            </p>
          </div>

          {/* Premium Gate */}
          {!hasPhotoAccess && (
            <div className="bg-gradient-to-r from-violet-500 to-pink-500 rounded-2xl p-8 text-center text-white mb-8">
              <Lock className="w-12 h-12 mx-auto mb-4 opacity-90" />
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Premium Feature
              </h2>
              <p className="text-violet-100 mb-6 max-w-md mx-auto">
                Photo to Coloring Page is available for Basic, Plus and Pro plan members. 
                Upgrade now to unlock this amazing feature!
              </p>
              <Button
                onClick={() => navigate("/pricing")}
                className="h-12 px-8 rounded-full bg-white text-violet-600 font-semibold hover:bg-violet-50"
                data-testid="upgrade-btn"
              >
                Upgrade Now
              </Button>
            </div>
          )}

          {/* Usage Info for Premium Users */}
          {hasPhotoAccess && usageInfo && (
            <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-pink-100 flex items-center justify-center">
                    <Camera className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">Photo Conversions</p>
                    <p className="text-xs text-slate-500">Resets {usageInfo.reset_date}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-violet-600">
                    {usageInfo.photo_conversions_remaining}/{usageInfo.photo_conversions_limit}
                  </p>
                  <p className="text-xs text-slate-500">remaining this month</p>
                </div>
              </div>
              {/* Progress Bar */}
              <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-violet-500 to-pink-500 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${Math.min(100, (usageInfo.photo_conversions_used / usageInfo.photo_conversions_limit) * 100)}%` 
                  }}
                />
              </div>
              {usageInfo.photo_conversions_remaining <= 3 && usageInfo.photo_conversions_remaining > 0 && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Running low! Consider upgrading for more conversions.
                </p>
              )}
              {usageInfo.photo_conversions_remaining === 0 && (
                <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Limit reached! <Link to="/pricing" className="underline">Upgrade</Link> for more.
                </p>
              )}
            </div>
          )}

          {/* Main Interface */}
          <div className={`grid md:grid-cols-2 gap-8 ${!hasPhotoAccess ? 'opacity-50 pointer-events-none' : ''}`}>
            {/* Upload Section */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-violet-600" />
                Upload Photo
              </h2>

              {/* Drop Zone */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                  previewUrl 
                    ? 'border-violet-300 bg-violet-50' 
                    : 'border-slate-200 hover:border-violet-300 hover:bg-violet-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  data-testid="photo-input"
                />

                {previewUrl ? (
                  <div className="relative">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="max-h-48 mx-auto rounded-lg object-contain"
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearImage();
                      }}
                      className="absolute top-2 right-2 h-8 w-8 p-0 rounded-full"
                      data-testid="clear-image-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="py-8">
                    <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600 font-medium mb-1">
                      Drop your photo here
                    </p>
                    <p className="text-slate-400 text-sm">
                      or click to browse (max 10MB)
                    </p>
                  </div>
                )}
              </div>

              {/* Style Selection */}
              <div className="mt-6">
                <label className="text-sm font-medium text-slate-700 mb-3 block">
                  Choose Style
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {styles.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setStyle(s.id)}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        style === s.id
                          ? 'border-violet-500 bg-violet-50'
                          : 'border-slate-200 hover:border-violet-200'
                      }`}
                      data-testid={`style-${s.id}`}
                    >
                      <span className="text-xl mb-1 block">{s.icon}</span>
                      <span className="text-sm font-medium text-slate-800 block">{s.label}</span>
                      <span className="text-xs text-slate-500">{s.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Convert Button */}
              <Button
                onClick={handleConvert}
                disabled={!previewUrl || isConverting || !hasPhotoAccess || (usageInfo && usageInfo.photo_conversions_remaining <= 0)}
                className="w-full h-12 mt-6 rounded-full bg-gradient-to-r from-violet-600 to-pink-600 text-white font-semibold"
                data-testid="convert-btn"
              >
                {isConverting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyzing & Converting... (30-60 sec)
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Convert to Coloring Page
                  </>
                )}
              </Button>
            </div>

            {/* Result Section */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Palette className="w-5 h-5 text-pink-600" />
                Result
              </h2>

              <div className="aspect-square rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden">
                {isConverting ? (
                  <div className="text-center p-8">
                    <Loader2 className="w-12 h-12 text-violet-400 mx-auto mb-4 animate-spin" />
                    <p className="text-slate-500 font-medium">Creating your coloring page...</p>
                    <p className="text-slate-400 text-sm mt-1">This may take up to a minute</p>
                  </div>
                ) : resultImage ? (
                  <img
                    src={resultImage}
                    alt="Coloring Page Result"
                    className="w-full h-full object-contain"
                    data-testid="result-image"
                  />
                ) : (
                  <div className="text-center p-8">
                    <Palette className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-400">
                      Your coloring page will appear here
                    </p>
                  </div>
                )}
              </div>

              {/* Download Button */}
              {resultImage && (
                <div className="mt-4 space-y-3">
                  <Button
                    onClick={handleDownload}
                    className="w-full h-12 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold"
                    data-testid="download-btn"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download Coloring Page
                  </Button>
                  
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                    <Check className="w-4 h-4 text-green-500" />
                    Saved to your library
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tips Section */}
          <div className="mt-12 bg-gradient-to-r from-violet-50 to-pink-50 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Tips for Best Results
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-violet-600 font-bold">1</span>
                </div>
                <div>
                  <p className="font-medium text-slate-800">Clear Subject</p>
                  <p className="text-sm text-slate-600">Use photos with a clear main subject and simple background</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-pink-600 font-bold">2</span>
                </div>
                <div>
                  <p className="font-medium text-slate-800">Good Lighting</p>
                  <p className="text-sm text-slate-600">Well-lit photos produce cleaner line art</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-violet-600 font-bold">3</span>
                </div>
                <div>
                  <p className="font-medium text-slate-800">Choose Style</p>
                  <p className="text-sm text-slate-600">Simple for kids, Detailed for adults</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PhotoToColoring;
